import React, { useMemo, useState } from 'react';
import type { TripResponseArgs } from '@this/domain/trip/trip2';
import type { TravelerInformationResponseArgs } from '@this/domain/traveler_information';
import type { TransportElementResponseArgs } from '@this/domain/transport_element2';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { first } from '@this/src/util';
import type { ProposalBundle } from '../api/proposalBundles';
import type { ReservationInfo } from '../api/reservationInfo';
import type { Blocker, DecidedField } from '../types/conversationState';
import LatestProposals from './LatestProposals';
import InputSupportSection from './InputSupportSection';
import { ORDER_ITEM_CATEGORY, isFlightCategory } from '../types/orderItemCategory';
import TripApplicationEditModal from './TripApplicationEditModal';
import TripApplicationInfoSection from './TripApplicationInfoSection';
import ReservationInfoSection from './ReservationInfoSection';
import { useTravelRules } from '../hooks/useTravelRules';

type InputSupportProps = {
  blockers: Blocker[];
  decidedFields: DecidedField[];
  searchTargets: string[];
  readyToSearch: boolean;
  isLoading: boolean;
  isArrangementRequested: boolean;
  hasConversationState: boolean;
  onChipClick: (template: string) => void;
  onBulkInsert: (template: string) => void;
};

interface Props {
  tripId: string;
  trip: TripResponseArgs;
  proposalBundles?: ProposalBundle[];
  isEditDisabled: boolean;
  reservationInfo: ReservationInfo | null;
  onTripUpdated?: () => void;
  inputSupport?: InputSupportProps;
}

const mergeTravelerInfos = (
  travelers: TravelerInformationResponseArgs[]
): TravelerInformationResponseArgs | null => {
  const firstTraveler = first(travelers);
  if (!firstTraveler) return null;
  if (travelers.length === 1) return firstTraveler;

  const initial: Record<string, unknown> = { ...firstTraveler };
  return travelers.slice(1).reduce((merged, t) => {
    Object.entries(t).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '' && !acc[key]) {
        acc[key] = value;
      }
      return acc;
    }, merged);
    return merged;
  }, initial) as TravelerInformationResponseArgs;
};

const InfoPanel = ({
  tripId,
  trip,
  proposalBundles = [],
  isEditDisabled = false,
  reservationInfo,
  onTripUpdated,
  inputSupport
}: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const { travelRules } = useTravelRules();
  const includeTax = travelRules?.organizationRules.includeTax ?? true;
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleEditClick = () => {
    if (!isEditDisabled && reservationInfo) {
      setIsEditModalOpen(true);
    }
  };

  const handleEditSuccess = () => {
    onTripUpdated?.();
  };

  const orderItems = useMemo(() => trip.current_order?.order_items || [], [trip.current_order?.order_items]);

  const hotelTravelerInfo = useMemo((): TravelerInformationResponseArgs | null => {
    const hotelItem = orderItems.find(item => item.order_item_category === ORDER_ITEM_CATEGORY.HOTEL);
    const travelers = hotelItem?.traveler_informations || [];
    return mergeTravelerInfos(travelers);
  }, [orderItems]);

  const flightTravelerInfo = useMemo((): TravelerInformationResponseArgs | null => {
    const flightItem = orderItems.find(item => isFlightCategory(item.order_item_category));
    const travelers = flightItem?.traveler_informations || [];
    return mergeTravelerInfos(travelers);
  }, [orderItems]);

  const shinkansenTravelerInfo = useMemo((): TravelerInformationResponseArgs | null => {
    const shinkansenItem = orderItems.find(item => item.order_item_category === ORDER_ITEM_CATEGORY.SHINKANSEN);
    const travelers = shinkansenItem?.traveler_informations || [];
    return mergeTravelerInfos(travelers);
  }, [orderItems]);

  const transportInfo = useMemo((): TransportElementResponseArgs[] => {
    return orderItems.flatMap(item => item.transports || []);
  }, [orderItems]);

  const hasHotelReservation = useMemo((): boolean => {
    return orderItems.some(item => (item.hotels || []).length > 0);
  }, [orderItems]);

  const hasFlightTransport = useMemo((): boolean => {
    return transportInfo.some(t => isFlightCategory(t.transport_type));
  }, [transportInfo]);

  const hasShinkansenTransport = useMemo((): boolean => {
    return transportInfo.some(t => t.transport_type === ORDER_ITEM_CATEGORY.SHINKANSEN);
  }, [transportInfo]);

  const showInputSupportInPanel = proposalBundles.length === 0 && inputSupport != null;

  return (
    <>
      <div style={styles.container}>
        {showInputSupportInPanel ? (
          <section style={styles.section}>
            <InputSupportSection {...inputSupport} />
          </section>
        ) : (
          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>このチャットの最新の提案内容</h3>
            <LatestProposals bundles={proposalBundles} includeTax={includeTax} />
          </section>
        )}
        <section style={styles.section}>
          <TripApplicationInfoSection
            trip={trip}
            isEditDisabled={isEditDisabled}
            canEdit={!!reservationInfo}
            onEditClick={handleEditClick}
          />
        </section>
        <section style={styles.section}>
          <h3 style={styles.sectionTitle}>予約手配に関する情報</h3>
          <ReservationInfoSection
            hotelTravelerInfo={hotelTravelerInfo}
            flightTravelerInfo={flightTravelerInfo}
            shinkansenTravelerInfo={shinkansenTravelerInfo}
            hasHotelReservation={hasHotelReservation}
            hasFlightTransport={hasFlightTransport}
            hasShinkansenTransport={hasShinkansenTransport}
          />
        </section>
      </div>
      {reservationInfo && (
        <TripApplicationEditModal
          tripId={tripId}
          trip={trip}
          reservationInfo={reservationInfo}
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={handleEditSuccess}
        />
      )}
    </>
  );
};

const getStyles = (theme: AITTheme) => ({
  container: {
    width: '320px',
    padding: '20px',
    backgroundColor: theme.contentBgColor,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    overflowY: 'auto' as const,
    flexShrink: 0,
    minHeight: 0
  },
  section: {
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '8px',
    padding: '16px',
    backgroundColor: theme.whiteColor
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600 as const,
    margin: 0
  }
});

export default InfoPanel;
